"use client"

import { useState } from "react"
import type { Message } from "ai/react"
import { cn } from "../lib/utils"
import { ChevronDown, ChevronUp, Search, Code, Terminal } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card"
import { Badge } from "../ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip"

export function IntermediateStep({ message }: { message: Message }) {
  const [expanded, setExpanded] = useState(false)

  try {
    const parsedInput = JSON.parse(message.content)
    const action = parsedInput.action || {}
    const observation = parsedInput.observation || ""
    const actionName = action.name || "unknown"

    // Format JSON for better display
    const formatJSON = (obj: any) => {
      try {
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return String(obj)
      }
    }

    const formattedArgs = formatJSON(action.args)

    return (
      <Card className="mb-6 mx-auto max-w-3xl bg-muted/30 border-muted backdrop-blur-sm overflow-hidden">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-1.5 rounded-full">
                <Search className="h-4 w-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  Agent Action
                  <Badge variant="outline" className="ml-2 font-mono text-xs">
                    {actionName}
                  </Badge>
                </CardTitle>
                <CardDescription className="text-xs">
                  {expanded ? "Click to collapse details" : "Click to expand details"}
                </CardDescription>
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={expanded ? "Collapse details" : "Expand details"}
                  >
                    {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>{expanded ? "Collapse details" : "Expand details"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <CardContent className="space-y-4 text-sm pt-0">
                <div>
                  <div className="flex items-center gap-1.5 font-medium text-xs text-muted-foreground mb-1.5">
                    <Code className="h-3.5 w-3.5" />
                    <span>Input:</span>
                  </div>
                  <div className="bg-background/50 p-3 rounded-md overflow-x-auto max-h-[120px] font-mono text-xs">
                    <pre className="whitespace-pre-wrap">{formattedArgs}</pre>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-1.5 font-medium text-xs text-muted-foreground mb-1.5">
                    <Terminal className="h-3.5 w-3.5" />
                    <span>Output:</span>
                  </div>
                  <div
                    className={cn(
                      "p-3 rounded-md overflow-x-auto max-h-[200px] font-mono text-xs",
                      observation ? "bg-background/50" : "bg-yellow-100/50 dark:bg-yellow-900/20",
                    )}
                  >
                    <pre className="whitespace-pre-wrap">{observation || "No result returned"}</pre>
                  </div>
                </div>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    )
  } catch (e) {
    // Error handling for invalid JSON
    return (
      <Card className="mb-6 mx-auto max-w-3xl bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-900/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-red-700 dark:text-red-300">
            Error Parsing Intermediate Step
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-red-600 dark:text-red-300">
            Failed to parse step data. Raw content: {message.content.substring(0, 100)}
            {message.content.length > 100 ? "..." : ""}
          </p>
        </CardContent>
      </Card>
    )
  }
}
